import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Popover from '@radix-ui/react-popover';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Layers, MoreHorizontal, Pin, PinOff, Search, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { ListResponse, TicketSummary } from '@aurora/contracts';
import { post, type ApiError } from '../../lib/api';
import { cn } from '../../lib/cn';
import { useSession } from '../../lib/session';
import { Button, Input, Spinner } from '../../components/ui';
import { useConfirm } from '../../components/dialog';
import { useToast } from '../../components/toast';
import { ticketKeys } from './list-cache';
import { asFilterNode } from './list-filters';
import { canDeleteView, canEditView, useViewMutations, type SavedView } from './views-api';
import { RenameViewDialog } from './views-dialogs';

/**
 * Choosing which list to look at.
 *
 * Pinned views sit in a row of tabs, one click each, with a live count; every
 * other view — the built-in queues, your own, the ones colleagues shared — is
 * a click away in the Views menu, where pinning, renaming and deleting live.
 * A row rather than a side rail, so the table keeps its width on a laptop.
 */

/** Which list is showing: everything, the trash, or one saved view. */
export type ActiveList = { kind: 'all' } | { kind: 'trash' } | { kind: 'view'; view: SavedView };

export function viewHref(view: SavedView | 'all' | 'trash'): string {
  if (view === 'all') return '/tickets';
  if (view === 'trash') return '/tickets?view=trash';
  return `/tickets?view=${view.id}`;
}

const TAB =
  'inline-flex h-7 max-w-56 shrink-0 items-center gap-1.5 rounded-control px-2.5 text-body transition-colors';
const TAB_IDLE = 'text-ink-muted hover:bg-sunken hover:text-ink';
const TAB_ACTIVE = 'bg-accent-soft font-medium text-accent-ink';

export function ViewTabs({
  views,
  viewsState,
  active,
  modified,
  canTrash,
  onRetry,
}: {
  views: SavedView[];
  viewsState: 'loading' | 'error' | 'ready';
  active: ActiveList;
  /** The active view has changes on top of what was saved. */
  modified: boolean;
  canTrash: boolean;
  onRetry: () => void;
}) {
  const pinned = views.filter((view) => view.is_pinned);
  const activeView = active.kind === 'view' ? active.view : null;
  const showActiveSeparately = activeView !== null && !activeView.is_pinned;

  return (
    <nav aria-label="Ticket views" className="mt-3 flex min-w-0 items-center gap-1">
      <div className="scrollbar-slim flex min-w-0 items-center gap-1 overflow-x-auto pb-0.5">
        <Link
          to={viewHref('all')}
          aria-current={active.kind === 'all' ? 'page' : undefined}
          className={cn(TAB, active.kind === 'all' ? TAB_ACTIVE : TAB_IDLE)}
        >
          All tickets
        </Link>
        {pinned.map((view) => (
          <ViewTab key={view.id} view={view} active={activeView?.id === view.id} modified={modified} />
        ))}
        {showActiveSeparately && <ViewTab view={activeView} active modified={modified} />}
        {active.kind === 'trash' && (
          <span aria-current="page" className={cn(TAB, TAB_ACTIVE)}>
            <Trash2 aria-hidden className="size-3.5" />
            Trash
          </span>
        )}
      </div>
      <ViewsMenu views={views} viewsState={viewsState} active={active} canTrash={canTrash} onRetry={onRetry} />
    </nav>
  );
}

function ViewTab({ view, active, modified }: { view: SavedView; active: boolean; modified: boolean }) {
  const filter = asFilterNode(view.filter);
  // The count is the view as saved, refreshed with the list. Built on the same
  // search as the list, under the same permission, so the two agree.
  const count = useQuery<ListResponse<TicketSummary>, ApiError>({
    queryKey: ticketKeys.count(`view-${view.id}`, { filter, q: view.search_query ?? null }),
    queryFn: () =>
      post('/tickets/search', {
        ...(filter ? { filter } : {}),
        ...(view.search_query ? { q: view.search_query } : {}),
        per_page: 1,
      }),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const total = count.data?.meta.total;
  const edited = active && modified;
  // Spelled out for assistive technology as one phrase; the badge alone reads
  // as a bare number run into the name.
  const label = [view.name, edited ? 'edited' : null, typeof total === 'number' ? `${total.toLocaleString()} tickets` : null]
    .filter(Boolean)
    .join(', ');

  return (
    <Link
      to={viewHref(view)}
      aria-current={active ? 'page' : undefined}
      aria-label={label}
      title={view.description ?? undefined}
      className={cn(TAB, active ? TAB_ACTIVE : TAB_IDLE)}
    >
      <span dir="auto" className="truncate">
        {view.name}
      </span>
      {edited && (
        <span aria-hidden className="text-mini font-normal text-ink-subtle" title="Changed since it was saved">
          (edited)
        </span>
      )}
      {typeof total === 'number' && (
        <span
          aria-hidden
          className={cn(
            'rounded-full px-1.5 text-mini tabular-nums',
            active ? 'bg-accent-line/40 text-accent-ink' : 'bg-sunken text-ink-muted',
          )}
        >
          {total.toLocaleString()}
        </span>
      )}
    </Link>
  );
}

function groupViews(views: SavedView[]) {
  return [
    { label: 'Pinned', views: views.filter((view) => view.is_pinned) },
    { label: 'Yours', views: views.filter((view) => !view.is_pinned && view.is_mine) },
    {
      label: 'Shared by colleagues',
      views: views.filter((view) => !view.is_pinned && !view.is_mine && !view.is_system),
    },
    { label: 'Built in', views: views.filter((view) => !view.is_pinned && view.is_system) },
  ].filter((group) => group.views.length > 0);
}

function ViewsMenu({
  views,
  viewsState,
  active,
  canTrash,
  onRetry,
}: {
  views: SavedView[];
  viewsState: 'loading' | 'error' | 'ready';
  active: ActiveList;
  canTrash: boolean;
  onRetry: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Held here, outside the popover, so the dialog outlives the menu closing.
  const [renaming, setRenaming] = useState<SavedView | null>(null);
  const shown = query.trim()
    ? views.filter((view) => view.name.toLowerCase().includes(query.trim().toLowerCase()))
    : views;
  const groups = groupViews(shown);
  const activeId = active.kind === 'view' ? active.view.id : null;

  return (
    <>
    {renaming && <RenameViewDialog view={renaming} onClose={() => setRenaming(null)} />}
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <Popover.Trigger asChild>
        <Button size="sm" variant="ghost" className="shrink-0" aria-expanded={open}>
          <Layers aria-hidden className="size-3.5" />
          Views
          <ChevronDown aria-hidden className="size-3" />
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className="animate-pop z-50 w-80 max-w-[calc(100vw-2rem)] rounded-card border border-line bg-raised p-1.5 shadow-float"
        >
          {views.length > 8 && (
            <div className="relative mb-1.5">
              <Search
                aria-hidden
                className="pointer-events-none absolute start-2 top-1/2 size-3.5 -translate-y-1/2 text-ink-subtle"
              />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Find a view…"
                aria-label="Find a view"
                className="h-7 ps-7 text-mini"
              />
            </div>
          )}

          <div className="scrollbar-slim max-h-[60vh] overflow-y-auto">
            {viewsState === 'loading' && (
              <div className="px-2 py-3">
                <Spinner label="Loading views" />
              </div>
            )}
            {viewsState === 'error' && (
              <div role="alert" className="flex items-center justify-between gap-2 px-2 py-2">
                <span className="text-mini text-critical-ink">Couldn’t load your views.</span>
                <Button size="sm" variant="secondary" onClick={onRetry}>
                  Try again
                </Button>
              </div>
            )}
            {viewsState === 'ready' && views.length === 0 && (
              <p className="px-2 py-2 text-mini text-ink-muted">
                No saved views yet. Filter the list the way you want it, then choose “Save view”.
              </p>
            )}
            {viewsState === 'ready' && views.length > 0 && groups.length === 0 && (
              <p className="px-2 py-2 text-mini text-ink-muted">No view is called that.</p>
            )}
            {groups.map((group) => (
              <div key={group.label} className="mb-1">
                <p className="px-2 pt-1.5 pb-1 text-mini font-medium text-ink-subtle">{group.label}</p>
                <ul>
                  {group.views.map((view) => (
                    <ViewRow
                      key={view.id}
                      view={view}
                      active={view.id === activeId}
                      onNavigate={() => setOpen(false)}
                      onRename={() => {
                        setOpen(false);
                        setRenaming(view);
                      }}
                    />
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-1 border-t border-line pt-1">
            <MenuLink to={viewHref('all')} active={active.kind === 'all'} onNavigate={() => setOpen(false)}>
              All tickets
            </MenuLink>
            {canTrash && (
              <MenuLink to={viewHref('trash')} active={active.kind === 'trash'} onNavigate={() => setOpen(false)}>
                <Trash2 aria-hidden className="size-3.5 text-ink-subtle" />
                Trash
              </MenuLink>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
    </>
  );
}

function MenuLink({
  to,
  active,
  onNavigate,
  children,
}: {
  to: string;
  active: boolean;
  onNavigate: () => void;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex items-center gap-2 rounded px-2 py-1.5 text-body transition-colors hover:bg-sunken',
        active ? 'font-medium text-accent-ink' : 'text-ink',
      )}
    >
      {children}
    </Link>
  );
}

function ViewRow({
  view,
  active,
  onNavigate,
  onRename,
}: {
  view: SavedView;
  active: boolean;
  onNavigate: () => void;
  onRename: () => void;
}) {
  const { can } = useSession();
  const toast = useToast();
  const confirm = useConfirm();
  const { pin, remove } = useViewMutations();
  const editable = canEditView(view, can) && (view.visibility === 'private' || view.visibility === 'shared');
  const deletable = canDeleteView(view, can);

  const togglePin = () =>
    pin.mutate(
      { id: view.id, pinned: !view.is_pinned },
      { onError: (error) => toast.error(error) },
    );

  const deleteView = async () => {
    const shared = view.visibility !== 'private';
    const ok = await confirm({
      title: `Delete the view “${view.name}”?`,
      description: `It disappears for you${shared ? ' and for everyone it is shared with' : ''}. The tickets in it are not touched.`,
      confirmLabel: 'Delete view',
      tone: 'danger',
    });
    if (!ok) return;
    remove.mutate(view.id, {
      onSuccess: () => toast.success(`Deleted “${view.name}”`),
      onError: (error) => toast.error(error),
    });
  };

  return (
    <li className="group/view flex items-center gap-0.5 rounded hover:bg-sunken">
      <Link
        to={viewHref(view)}
        onClick={onNavigate}
        aria-current={active ? 'page' : undefined}
        title={view.description ?? undefined}
        className={cn(
          'flex min-w-0 flex-1 flex-col rounded px-2 py-1.5 text-start',
          active ? 'font-medium text-accent-ink' : 'text-ink',
        )}
      >
        <span dir="auto" className="truncate text-body">
          {view.name}
        </span>
        {(view.description || (!view.is_mine && view.owner)) && (
          <span dir="auto" className="truncate text-mini font-normal text-ink-subtle">
            {view.description ?? `Shared by ${view.owner?.name}`}
          </span>
        )}
      </Link>
      <Button
        size="icon"
        variant="ghost"
        className="size-7 shrink-0"
        onClick={togglePin}
        aria-pressed={view.is_pinned}
        aria-label={view.is_pinned ? `Unpin “${view.name}”` : `Pin “${view.name}” to the tabs`}
        title={view.is_pinned ? 'Unpin from the tabs' : 'Pin to the tabs'}
      >
        {view.is_pinned ? (
          <PinOff aria-hidden className="size-3.5 text-accent" />
        ) : (
          <Pin aria-hidden className="size-3.5" />
        )}
      </Button>
      {(editable || deletable) && (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <Button size="icon" variant="ghost" className="size-7 shrink-0" aria-label={`More for “${view.name}”`}>
              <MoreHorizontal aria-hidden className="size-3.5" />
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={4}
              className="animate-pop z-[60] min-w-40 rounded-card border border-line bg-raised p-1 shadow-float"
            >
              {editable && (
                <DropdownMenu.Item
                  onSelect={onRename}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-body text-ink outline-none data-highlighted:bg-sunken"
                >
                  Rename…
                </DropdownMenu.Item>
              )}
              {deletable && (
                <DropdownMenu.Item
                  onSelect={() => void deleteView()}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-body text-critical-ink outline-none data-highlighted:bg-critical-soft"
                >
                  <Trash2 aria-hidden className="size-3.5" />
                  Delete…
                </DropdownMenu.Item>
              )}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      )}
    </li>
  );
}
